import React, { useMemo, useState } from "react";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SupervisedUserCircleOutlinedIcon from "@mui/icons-material/SupervisedUserCircleOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import Box from "@mui/material/Box";
import { Alert } from "@components/Alert/Alert";
import Badge from "@components/Badge/Badge";
import Button from "@components/Button/Button";
import DocumentCard from "@components/DocumentCard/DocumentCard";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import type { DrawerType } from "@components/SideDrawer/SideDrawer.types.ts";
import Subheader from "@components/Subheader/Subheader";
import {
  CertificatesContexts,
  getVesselCertificates,
  VRCertificateTypes,
  type VesselTypeCode,
} from "@constants/certificates";
import { useNotification } from "@context/NotificationContext";
import { useVesselCallFormStore } from "@features/vessel-call/create/store/useVesselCallForm.store";
import { hasAlreadyActiveVesselCall } from "@features/vessel-call/vessel-scan/utils";
import { useVesselRegistration } from "@features/vessel-register/hooks/useVesselRegistration.tsx";
import type { VesselRegisterFormData } from "@features/vessel-register/schema/vesselRegisterFormSchema";
import {
  transformNumberToString,
  useVesselRegisterFormStore,
} from "@features/vessel-register/store/useVesselRegisterForm.store";
import { FORM_FIELDS } from "@features/vessel-register/utils";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { PreviousShippingAgencyTransfersResponse } from "@services/shipping-agency-transfer/types";
import { getTransfersForVessel } from "@services/shippingAgencyTransferApi";
import type {
  AdditionalDocument,
  AdditionalDocumentType,
  BunkerBargeCertificateType,
  BunkerBargeDocument,
  BunkerBargeDocumentType,
  Vessel,
} from "@services/vessel/types";
import { checkDraftVesselCall } from "@services/vesselCallApi";
import { getVesselById } from "@services/vesselsApi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateToDDMMYYYY } from "@utils/date-time";
import mapVesselResponseToFormData from "@utils/mappers/vesselResponseToFormData.ts";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import { getVesselCategory } from "@utils/vessel-category";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useClaimTask } from "./hooks/useClaimTask";
import { useCompleteTask } from "./hooks/useCompleteTask";
import { useDownloadCertificate } from "./hooks/useDownloadCertificate";
import ApproveRejectSideDrawer from "./side-drawers/ApproveReject";
import ApproveUpdateBunkerBargeSideDrawer from "./side-drawers/ApproveUpdateBunkerBarge";
import DelegateSideDrawer from "./side-drawers/Delegate";
import DelegateDetailsSideDrawer from "./side-drawers/DelegateDetails";
import DetailsSideDrawer from "./side-drawers/Details";
import RestrictionStatusSideDrawer from "./side-drawers/RestrictionStatus";

export const RolesMapping: Record<string, string> = {
  MARINE_OFFICE: "marine-office",
  MARINE_OPERATOR: "marine-operator",
  MARINE_HARBOR_MASTER: "harbour-master",
  MARINE_SUPERVISOR: "marine-supervisor",
  SHIPPING_AGENT: "shipping-agent",
};

export const LANG_BASE = "vessel-management.vessel-status-page";
export const TEST_ID_BASE = "vessel-status-page";
export const TASK_STATUS = {
  CANCEL: "CANCEL",
  READY: "READY",
  CLAIMED: "CLAIMED",
  COMPLETED: "COMPLETED",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  DELEGATED: "DELEGATED",
  DELEGATION_ACCEPTED: "DELEGATION_ACCEPTED",
  DELEGATION_REJECTED: "DELEGATION_REJECTED",
};
const FLOW_STATUS = {
  DELEGATED: "DELEGATED",
  DELEGATION_ACCEPTED: "DELEGATION_ACCEPTED",
  DELEGATION_REJECTED: "DELEGATION_REJECTED",
};
const BUNKER_BARGE_CERTIFICATE_TITLE_KEYS: Record<BunkerBargeCertificateType, string> = {
  NAVIGATION_LICENSE: "form-sections.navigation-license",
  CHARTER_PARTY_AGREEMENT: "form-sections.charter-party-agreement",
  OIL_SPILL_COMPANY: "form-sections.oil-spill-company-details",
  SIRE_REPORT: "form-sections.sire-report",
  HOSE_TEST_CERTIFICATE: "form-sections.hose-test-certificate",
  SHIP_TO_SHIP_PLAN: "form-sections.ship-to-ship-plan",
};
const BUNKER_BARGE_DOCUMENT_TYPE_LABEL_KEYS: Record<BunkerBargeDocumentType, string> = {
  CERTIFICATE: "certificate",
  PERMIT: "permit",
  DOCUMENT: "document",
  INSURANCE: "insurance",
  VESSEL_MASTER_SOF: "vessel-master-sof",
  PRE_SAILING_MASTER_DECLARATION: "pre-sailing-master-declaration",
  OTHER_SPECIFY: "other-specify",
};
const ADDITIONAL_DOCUMENT_TYPE_LABEL_KEYS: Record<AdditionalDocumentType, string> =
  BUNKER_BARGE_DOCUMENT_TYPE_LABEL_KEYS;
export const service = "bff-vessel";
export const serviceVesselCall = "bff-vessel-call";

export const VESSEL_STATUS = {
  APPROVED: "APPROVED",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  REJECTED: "REJECTED",
  DRAFT: "DRAFT",
};

const RESTRICTIONS_STATUS = {
  CLEARTOOPERATE: "CLEARTOOPERATE",
  RELEASED: "RELEASED",
};

type SupportedDrawerType = Extract<
  DrawerType,
  "approval" | "reject" | "status" | "details" | "statusDetails"
>;
type BunkerBargeDrawerType = "approve" | "update";

type DrawerText = {
  title: string;
  notesLabel?: string;
  submit: string;
  description?: string;
  fileUploadTitle?: string;
  additionalFileUploadTitle?: string;
  staticContent?: string;
  dropdownLabel?: string;
  externalNotesLabel?: string;
  externalAttachmentUploadTitle?: string;
  internalNotesLabel?: string;
  internalAttachmentUploadTitle?: string;
  approveSuccessMessage?: string;
  rejectSuccessMessage?: string;
  successMessage?: string;
  errorMessage?: string;
};

type VesselCallRedirectDecision = {
  targetPath: string;
  state: {
    vessel: {
      id?: string;
    };
  };
};

export const DRAWER_TEXT = (details?: Vessel): Record<SupportedDrawerType, DrawerText> => ({
  approval: {
    title: "approveTitle",
    description: "approveDescription",
    externalNotesLabel: "externalNotes",
    externalAttachmentUploadTitle: "externalAttachments",
    internalNotesLabel: "internalNotes",
    internalAttachmentUploadTitle: "internalAttachments",
    submit: "approve",
    successMessage: "vessel-approved-success-title",
    errorMessage: "vessel-approved-error-title",
  },
  reject: {
    title: "rejectTitle",
    description: "rejectDescription",
    externalNotesLabel: "externalNotes",
    externalAttachmentUploadTitle: "externalAttachments",
    internalNotesLabel: "internalNotes",
    internalAttachmentUploadTitle: "internalAttachments",
    submit: "reject",
    successMessage: "vessel-rejected-success-title",
    errorMessage: "vessel-rejected-error-title",
  },
  status: {
    title: "statusTitle",
    description: "statusDescription",
    notesLabel: "statusReason",
    submit: "change",
    dropdownLabel: "statusLabel",
  },
  details: {
    title: "detailsTitle",
    notesLabel: "statusReason",
    submit: "ok",
    staticContent: details?.statusChangeReason,
  },
  statusDetails: {
    title: "detailsTitle",
    externalNotesLabel: "externalNotes",
    externalAttachmentUploadTitle: "externalAttachments",
    internalNotesLabel: "internalNotes",
    internalAttachmentUploadTitle: "internalAttachments",
    submit: "ok",
  },
});

const VesselStatus: React.FC = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { userAccessPermissions, userActionPermissions, user } = useAuthStore();

  const isMarineDepartment = checkPermission(userAccessPermissions, "hasTaskAccess");
  const isMarineSupervisor = user?.roles.includes(RolesMapping["MARINE_SUPERVISOR"]);
  const isMarineOffice = user?.roles.includes(RolesMapping["MARINE_OFFICE"]);

  const { setField } = useVesselCallFormStore.getState();
  const [drawerType, setDrawerType] = useState<DrawerType | null>(null);

  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [isDelegateDrawerOpen, setIsDelegateDrawerOpen] = useState(false);
  const [isDelegateDetailsDrawerOpen, setIsDelegateDetailsDrawerOpen] = useState(false);
  const [bunkerBargeDrawerType, setBunkerBargeDrawerType] = useState<BunkerBargeDrawerType | null>(
    null,
  );
  const [isRestrictionStatusDrawerOpen, setIsRestrictionStatusDrawerOpen] = useState(false);

  const { notify } = useNotification();
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { onDiscard } = useVesselRegistration({ vesselId: id });
  const [isConfirmDiscardDraftOpen, setIsConfirmDiscardDraftOpen] = useState(false);

  const handleSuccess = () => {
    setDrawerType(null);
    queryClient.invalidateQueries({ queryKey: ["vessel"] });
    queryClient.invalidateQueries({ queryKey: ["task"] });
    queryClient.invalidateQueries({ queryKey: ["vessels"] });
    queryClient.invalidateQueries({ queryKey: ["registeredVessels"] });
  };

  const { mutate: completeTask } = useCompleteTask({ handleSuccess });
  const { mutate: claimTask } = useClaimTask({ handleSuccess });

  const {
    data: details,
    isLoading,
    isError: isVesselError,
    error: vesselError,
  } = useQuery<Vessel>({
    queryKey: ["vessel", service, id],
    queryFn: () => getVesselById(service, id),
    gcTime: 0,
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  //if snapshotId exists in the vessel details, it means the current user is no longer the current owner of the vessel
  //snapshotId is only generated for vessels that have been transferred to another company
  const isCurrentUserOwnersMatters = useMemo(() => details?.snapshotId != null, [details]);

  const hasExistingVesselCall = details?.hasActiveVesselCall;

  const shouldFetchTransfers = useMemo(
    () => !!id && !!details && details.snapshotId == null,
    [id, details],
  );

  const { data: transfersDetails } = useQuery<PreviousShippingAgencyTransfersResponse>({
    queryKey: ["shipping-agency-transfers", service, id],
    queryFn: () =>
      getTransfersForVessel(serviceVesselCall, id || "", {
        pageNumber: 1,
        pageSize: 25,
        statuses: ["PENDING_APPROVAL", "PENDING_MARINE_DEPARTMENT_APPROVAL"],
      }),
    enabled: shouldFetchTransfers,
  });

  const isCurrentUserOwner = useMemo(() => {
    return user?.email === details?.task?.owner;
  }, [details, user]);

  const hasTransferPending = useMemo(
    () => (transfersDetails?.shippingAgencyTransfers?.length ?? 0) > 0,
    [transfersDetails],
  );

  const isTaskAssignedToRole = useMemo(
    () => !!user?.roles.includes(RolesMapping[details?.task?.assignedToRole ?? ""] ?? ""),
    [details, user],
  );

  const isRegistrationPending = useMemo(
    () => details?.vesselRegistrationStatus === VESSEL_STATUS.PENDING_APPROVAL,
    [details],
  );

  const isTaskClaimed = useMemo(() => details?.task?.taskStatus === TASK_STATUS.CLAIMED, [details]);

  const {
    internalNotes,
    externalNotes,
    internalNotesDocument,
    externalNotesDocument,
    internalNotesDocumentName,
    externalNotesDocumentName,
    submittedBy,
    createdBy,
    createdByRole,
    createdAt,
  } = useMemo(() => details?.historyNotes?.[0] || {}, [details]);

  const { statusChangeSubmittedByRole, statusChangeSubmittedByUser, statusChangeTime } = useMemo(
    () => details?.statusChangeDetails || {},
    [details],
  );

  const hasInternalExternalNotes = useMemo(
    () => internalNotes || externalNotes || internalNotesDocument || externalNotesDocument,
    [internalNotes, externalNotes, internalNotesDocument, externalNotesDocument],
  );

  const { handleDownload, handlePreview } = useDownloadCertificate();

  const vesselDetailsTrans = `${LANG_BASE}.vessel-details`;
  const techDetailsTrans = `${LANG_BASE}.technical-details`;
  const ownerShipTrans = `${LANG_BASE}.ownership`;
  const certificatesTrans = `${LANG_BASE}.certificates`;
  const bunkerBargeTrans = "vessel-management.bunker-barge-page";

  const transStyleClass = "font-normal text-[#646A77]";
  const wrapperStyleClass = "border-t border-[#F1F2F4] pt-3";
  const wrapperFirstStyleClass = "pt-3";

  const vesselMainInfo = `${details?.vesselImoNumber || "/"} - ${details?.vesselName || "/"}`;

  const isStatusDetailsDrawer = drawerType === "statusDetails";
  const statusChangeSubmittedBy = statusChangeSubmittedByUser || statusChangeSubmittedByRole;
  const statusChangeSubmittedByRoleValue = statusChangeSubmittedByUser
    ? statusChangeSubmittedByRole
    : undefined;

  const { setAll, resetForm } = useVesselRegisterFormStore();

  const getVesselCallRedirectDecision = async (
    vesselId?: string,
  ): Promise<VesselCallRedirectDecision> => {
    try {
      const response = await checkDraftVesselCall(vesselId ?? "");

      if (response?.vesselCallId) {
        return {
          targetPath: `/${paths.vesselCall.index}/vessel-call-status/${response.vesselCallId}`,
          state: {
            vessel: { id: response?.vesselCallId },
          },
        };
      }

      return {
        targetPath: `/${paths.vesselCall.index}/${paths.vesselCall.create}`,
        state: {
          vessel: { id: vesselId },
        },
      };
    } catch (error: unknown) {
      if (hasAlreadyActiveVesselCall(error)) {
        notify({
          title: t("vessel-call.vessel-scan.already-active-error-title"),
          description: t("vessel-call.vessel-scan.already-active-error-description"),
          type: "error",
        });
        console.error("Vessel call error:", error);
        throw error;
      }
      const responseDetail =
        typeof error === "object" && error !== null && "response" in error
          ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      const errorMessage = error instanceof Error ? error.message : undefined;

      notify({
        title: t("vessel-call.vessel-scan.scan-error-title"),
        description:
          responseDetail || errorMessage || t("vessel-call.vessel-scan.scan-error-description"),
        type: "error",
      });
      console.error("Error checking draft vessel call:", error);
      throw error;
    }
  };

  const handleAddVesselCall = async () => {
    const vesselId = details?.id;

    if (!vesselId) {
      return;
    }

    setField("vesselId", vesselId);

    const redirectDecision = await getVesselCallRedirectDecision(vesselId);

    navigate(redirectDecision.targetPath, {
      state: redirectDecision.state,
      replace: true,
    });
  };

  const handleUpdate = async () => {
    if (hasTransferPending) {
      notify({
        title: t(`${LANG_BASE}.notifications.update-vessel-error-title`),
        description: t(`${LANG_BASE}.notifications.update-vessel-error-description`),
        type: "error",
      });
      return;
    }

    resetForm();
    const uploadStore = useUploadStore.getState();
    Object.values(VRCertificateTypes).forEach((certKey) => {
      uploadStore.clearFilesForKey(certKey);
    });

    const formData: VesselRegisterFormData = mapVesselResponseToFormData(details!);
    const transformedFormData = transformNumberToString(formData);
    setAll(transformedFormData);

    const certs = getVesselCertificates(
      CertificatesContexts.BASE,
      formData.vesselTypeCode as VesselTypeCode,
      formData.grossTonnage,
    );

    await Promise.allSettled(
      certs.map(async ({ type }) => {
        const certificateUrl = formData[FORM_FIELDS.CERTIFICATES]?.[type]?.certificateUrl;
        const documentName = formData[FORM_FIELDS.CERTIFICATES]?.[type]?.certificateDocumentName;

        if (certificateUrl) {
          const filenameFallback = t(`vessel-management.certificates.${type}`);
          const uploadEntry = {
            name: documentName || filenameFallback,
            url: certificateUrl,
            fileName: documentName || filenameFallback,
          };
          uploadStore.setFilesForKey(type, [uploadEntry]);
        }
      }),
    );
    uploadStore.clearFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS);

    await Promise.allSettled(
      (details?.additionalDocuments ?? []).map(async (file) => {
        if (file.documentUrl) {
          const uploadEntry = {
            ...file,
            name: file.documentName || file.fileName || "attachment",
            url: file.documentUrl,
          };
          useUploadStore
            .getState()
            .pushFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS, [uploadEntry]);
        }
      }),
    );
    const bunkerBargeAdditionalDocuments = details?.bunkerBargeDetails?.additionalDocuments ?? [];

    if (bunkerBargeAdditionalDocuments.length) {
      uploadStore.setFilesForKey(
        FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS,
        bunkerBargeAdditionalDocuments.map((document) => ({
          name: document.fileName,
          url: document.documentUrl,
        })),
      );
    }
    navigate(
      `/${paths.vesselManagement.index}/${paths.vesselManagement.register}/${paths.vesselManagement.vesselInformation}`,
      { state: { vesselDetails: details } },
    );
  };

  const isUpdateButtonShown = useMemo(() => {
    if (isCurrentUserOwnersMatters) return false;

    if (!details?.userCanUpdate) return false;

    const status = details?.vesselRegistrationStatus;

    if (isMarineDepartment) {
      return status !== VESSEL_STATUS.PENDING_APPROVAL;
    }

    if (status === VESSEL_STATUS.APPROVED) return true;

    if (status === VESSEL_STATUS.REJECTED && isCurrentUserOwner) return true;

    return false;
  }, [details, isMarineDepartment, isCurrentUserOwner, isCurrentUserOwnersMatters]);

  const isClaimButtonShown = useMemo(
    () =>
      details?.task?.taskStatus === TASK_STATUS.READY &&
      details?.task?.flowStatus !== FLOW_STATUS.DELEGATED &&
      isTaskAssignedToRole,
    [details, isTaskAssignedToRole],
  );

  const isUnclaimButtonShown = useMemo(
    () =>
      isTaskClaimed && isCurrentUserOwner && details?.task?.flowStatus !== FLOW_STATUS.DELEGATED,
    [isTaskClaimed, isCurrentUserOwner, details],
  );

  const isEditButtonShown = useMemo(
    () =>
      details?.userCanUpdate &&
      (details?.vesselRegistrationStatus === VESSEL_STATUS.PENDING_APPROVAL ||
        details?.markedAsDraft),
    [details],
  );

  const isDiscardButtonShown = useMemo(
    () => details?.markedAsDraft && details?.userCanUpdate,
    [details],
  );

  const isDelegateButtonShown = useMemo(
    () => isTaskClaimed && isRegistrationPending && isCurrentUserOwner && isMarineSupervisor,
    [isTaskClaimed, isRegistrationPending, isCurrentUserOwner, isMarineSupervisor],
  );

  const isAcceptRejectDelegationButtonShown = useMemo(
    () =>
      isTaskAssignedToRole &&
      !isTaskClaimed &&
      details?.task?.flowStatus === FLOW_STATUS.DELEGATED &&
      details?.task?.taskStatus !== TASK_STATUS.COMPLETED,
    [isTaskAssignedToRole, details, isTaskClaimed],
  );

  const isAcceptRejectButtonShown = useMemo(
    () =>
      isTaskClaimed &&
      isRegistrationPending &&
      isCurrentUserOwner &&
      !isAcceptRejectDelegationButtonShown,
    [isTaskClaimed, isRegistrationPending, isCurrentUserOwner, isAcceptRejectDelegationButtonShown],
  );

  const isDelegateAlertShown = useMemo(
    () =>
      isTaskAssignedToRole &&
      details?.task?.taskStatus !== TASK_STATUS.COMPLETED &&
      details?.task?.flowStatus === FLOW_STATUS.DELEGATED,
    [isTaskAssignedToRole, details],
  );

  const isUpdateBunkerBargeButtonShown = useMemo(
    () =>
      isMarineOffice &&
      details?.task?.taskStatus === TASK_STATUS.COMPLETED &&
      details.vesselRegistrationStatus === VESSEL_STATUS.APPROVED,
    [details, isMarineOffice],
  );

  const isDraftAlertShown = useMemo(() => details?.markedAsDraft, [details]);
  const bunkerBargeDetails = details?.bunkerBargeDetails;
  const isBunkerBargeUpdate = bunkerBargeDrawerType === "update";

  const formatBunkerBargeDate = (date?: string) => (date ? formatDateToDDMMYYYY(date) : "-");

  const getBunkerBargeCompanyName = (company?: { companyName?: string; companyCode?: string }) =>
    company?.companyName || company?.companyCode || "-";

  const getBunkerBargeCertificate = (certificateType: BunkerBargeCertificateType) =>
    bunkerBargeDetails?.certificates?.find(
      (certificate) => certificate.certificateType === certificateType,
    );

  const getBunkerBargeDocumentTypeLabel = (document?: BunkerBargeDocument) => {
    if (!document?.documentType) return "-";

    const label = t(
      `${bunkerBargeTrans}.document-types.${BUNKER_BARGE_DOCUMENT_TYPE_LABEL_KEYS[document.documentType]}`,
    );

    if (document.documentType === "OTHER_SPECIFY" && document.otherSpecifyType) {
      return document.otherSpecifyType;
    }

    return label;
  };

  const getAdditionalDocumentTypeLabel = (document?: AdditionalDocument) => {
    if (!document?.documentType) return "-";

    if (document.documentType === "OTHER_SPECIFY" && document.otherSpecifyType) {
      return document.otherSpecifyType;
    }

    return t(
      `${bunkerBargeTrans}.document-types.${ADDITIONAL_DOCUMENT_TYPE_LABEL_KEYS[document.documentType]}`,
    );
  };

  const renderBunkerBargeInfoItem = ({
    testId,
    label,
    value,
    wrapperStyle,
  }: {
    testId: string;
    label: string;
    value?: string;
    wrapperStyle?: string;
  }) => (
    <PaperItem
      testId={testId}
      translate={label}
      text={value || "-"}
      textStyles="font-normal"
      tranStyle={transStyleClass}
      wrapperStyle={`px-3 ${wrapperStyle ?? "my-0 py-3"}`}
    />
  );

  const renderBunkerBargeCertificateCard = ({
    certificateType,
    issueLabel,
    expiryLabel,
    additionalFields,
  }: {
    certificateType: BunkerBargeCertificateType;
    issueLabel?: string;
    expiryLabel?: string;
    additionalFields?: Record<string, React.ReactNode>;
  }) => {
    const certificate = getBunkerBargeCertificate(certificateType);
    const resolvedIssueDate = certificate?.issueDate;
    const resolvedExpiryDate = certificate?.expiryDate;
    const resolvedExpiryStatus = certificate?.expiryStatus;
    const resolvedUploadDate = certificate?.uploadedAt;
    const hasAdditionalFields = Object.values(additionalFields ?? {}).some(
      (value) => value !== undefined && value !== null && value !== "",
    );

    if (
      !certificate?.documentUrl &&
      !certificate?.name &&
      !resolvedIssueDate &&
      !resolvedExpiryDate &&
      !hasAdditionalFields
    ) {
      return null;
    }

    const documentUrl = certificate?.documentUrl;
    const fileName = certificate?.name;

    return (
      <DocumentCard
        data-testid={`${TEST_ID_BASE}-bunker-barge-${certificateType.toLowerCase().replace(/_/g, "-")}`}
        title={t(`${bunkerBargeTrans}.${BUNKER_BARGE_CERTIFICATE_TITLE_KEYS[certificateType]}`)}
        fileName={fileName || "-"}
        rootClassName="bg-white"
        additionalFields={additionalFields}
        startDate={resolvedIssueDate}
        startDateTrans={`${issueLabel ?? t(`${bunkerBargeTrans}.from-date`)}: `}
        expiryDate={resolvedExpiryDate}
        expiryDateTrans={`${expiryLabel ?? t(`${bunkerBargeTrans}.to-date`)}: `}
        expiryStatus={resolvedExpiryStatus}
        uploadDate={resolvedUploadDate}
        uploadDateTrans={`${t(`${bunkerBargeTrans}.upload-date`)}: `}
        onDownload={
          documentUrl
            ? () => handleDownload("certificate", service, documentUrl, fileName)
            : undefined
        }
        onPreview={
          documentUrl ? () => handlePreview("certificate", service, documentUrl) : undefined
        }
      />
    );
  };

  const bunkerBargeAdditionalDocuments = bunkerBargeDetails?.additionalDocuments ?? [];

  if (isLoading) {
    return <LoadingOverlay loading={isLoading} />;
  }

  if (isVesselError) {
    return <ErrorHandler error={vesselError} testId={`${TEST_ID_BASE}-error-handler`} />;
  }

  return (
    <PageLayout dir={dir}>
      <Subheader
        testId={`${TEST_ID_BASE}-subheader`}
        subtitle={
          <div
            className="flex items-center space-x-2 text-sm text-gray-700"
            data-testid={`${TEST_ID_BASE}-breadcrumb`}
          >
            <Link
              to="/vessel-management"
              className="text-blue-600 hover:underline"
              data-testid={`${TEST_ID_BASE}-breadcrumb-link`}
            >
              {t("navigation.vessel-management")}
            </Link>
            <span className="text-gray-400">/</span>
            <span data-testid={`${TEST_ID_BASE}-breadcrumb-current`}>{vesselMainInfo}</span>
          </div>
        }
        title={
          <div className="flex items-end justify-between w-full">
            <div>
              <span data-testid={`${TEST_ID_BASE}-title`}>{vesselMainInfo}</span>
              <div className="flex items-center min-h-10">
                <div className="min-w-40 flex">
                  <Badge
                    testId={`${TEST_ID_BASE}-status`}
                    type={
                      details?.vesselRegistrationStatus === VESSEL_STATUS.APPROVED
                        ? "success"
                        : details?.vesselRegistrationStatus === VESSEL_STATUS.REJECTED
                          ? "error"
                          : details?.vesselRegistrationStatus === VESSEL_STATUS.DRAFT
                            ? "neutral"
                            : "warning"
                    }
                    label={
                      details?.vesselRegistrationStatus
                        ? t(`vessel-management.status-labels.${details?.vesselRegistrationStatus}`)
                        : t("common.unknown")
                    }
                  />
                  {hasInternalExternalNotes && (
                    <div className="flex items-center">
                      <span className="text-base ml-2">–</span>
                      <Button
                        testId={`${TEST_ID_BASE}-view-status-details`}
                        variant="text"
                        type="button"
                        className="ml-4 whitespace-nowrap"
                        onClick={() => setIsDetailsDrawerOpen(true)}
                        label={
                          <span className="normal-case font-medium">
                            {t(`${LANG_BASE}.view-details`)}
                          </span>
                        }
                      />
                    </div>
                  )}
                </div>
                {isMarineDepartment && (
                  <div className="flex items-center">
                    <Badge
                      testId={`${TEST_ID_BASE}-restriction`}
                      type={
                        [RESTRICTIONS_STATUS.CLEARTOOPERATE, RESTRICTIONS_STATUS.RELEASED].includes(
                          details?.vesselLogType?.vesselLogTypeCode ?? "",
                        )
                          ? "success"
                          : "warning"
                      }
                      label={
                        details?.vesselLogType?.vesselLogTypeCode
                          ? t(
                              `vessel-management.restrictionsLabels.${details?.vesselLogType?.vesselLogTypeCode}`,
                            )
                          : t("common.unknown")
                      }
                    />
                    {details?.statusChangeReason && (
                      <div className="flex items-center">
                        <span className="text-base ml-2">–</span>
                        <Button
                          testId={`${TEST_ID_BASE}-view-restriction-details`}
                          variant="text"
                          type="button"
                          className="whitespace-nowrap"
                          onClick={() => {
                            setIsDetailsDrawerOpen(true);
                            setDrawerType("statusDetails");
                          }}
                          label={
                            <span className="normal-case font-medium">
                              {t(`${LANG_BASE}.view-details`)}
                            </span>
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-4">
              {isUpdateButtonShown ? (
                <Button
                  data-testid={`${TEST_ID_BASE}-update`}
                  startIcon={<SyncIcon />}
                  label={t(`${LANG_BASE}.update`)}
                  variant="outlined"
                  onClick={handleUpdate}
                  sx={{ textTransform: "none" }}
                />
              ) : null}
              {isUpdateBunkerBargeButtonShown ? (
                <Button
                  data-testid={`${TEST_ID_BASE}-update-bunker-barge`}
                  startIcon={<SyncIcon />}
                  label={t(`${LANG_BASE}.update-bunker-barge`)}
                  variant="outlined"
                  onClick={() => setBunkerBargeDrawerType("update")}
                  sx={{ textTransform: "none" }}
                />
              ) : null}
              {isMarineDepartment && (
                <Button
                  data-testid={`${TEST_ID_BASE}-change-button`}
                  startIcon={<SyncIcon />}
                  label={t(`${LANG_BASE}.change-status`)}
                  variant="outlined"
                  onClick={() => setIsRestrictionStatusDrawerOpen(true)}
                  sx={{ textTransform: "none" }}
                />
              )}
              {isClaimButtonShown ? (
                <Button
                  data-testid={`${TEST_ID_BASE}-claim`}
                  startIcon={<ContentPasteGoIcon />}
                  label={t(`${LANG_BASE}.claim`)}
                  variant="contained"
                  onClick={() =>
                    claimTask({
                      action: "claim",
                      service,
                      vesselId: id ?? "",
                      errorTitle: t("vessel-management.notifications.task-claim-error-title"),
                      successTitle: t("vessel-management.notifications.task-claimed"),
                    })
                  }
                  sx={{ textTransform: "none" }}
                />
              ) : null}
              {isUnclaimButtonShown ? (
                <Button
                  data-testid={`${TEST_ID_BASE}-unclaim`}
                  label={t(`${LANG_BASE}.unclaim`)}
                  variant="outlined"
                  onClick={() =>
                    claimTask({
                      action: "unclaim",
                      service,
                      vesselId: id ?? "",
                      errorTitle: t("vessel-management.notifications.task-unclaim-error-title"),
                      successTitle: t("vessel-management.notifications.task-unclaimed"),
                    })
                  }
                  sx={{ textTransform: "none" }}
                />
              ) : null}
              {isAcceptRejectDelegationButtonShown ? (
                <Button
                  data-testid={`${TEST_ID_BASE}-reject-delegation`}
                  label={t(`${LANG_BASE}.reject-delegation`)}
                  startIcon={<HighlightOffIcon />}
                  variant="outlined"
                  onClick={() =>
                    completeTask({
                      service,
                      vesselId: id || "",
                      params: {
                        status: TASK_STATUS.DELEGATION_REJECTED,
                      },
                      successTitle: t(
                        "vessel-management.notifications.delegation-reject-success-title",
                      ),
                      errorTitle: t(
                        "vessel-management.notifications.delegation-reject-error-title",
                      ),
                    })
                  }
                  sx={{ textTransform: "none" }}
                />
              ) : null}
              {isAcceptRejectDelegationButtonShown ? (
                <Button
                  data-testid={`${TEST_ID_BASE}-accept-delegation`}
                  label={t(`${LANG_BASE}.accept-delegation`)}
                  startIcon={<ContentPasteGoIcon />}
                  variant="contained"
                  onClick={() =>
                    claimTask({
                      action: "claim",
                      service,
                      vesselId: id || "",
                      successTitle: t(
                        "vessel-management.notifications.delegation-accept-success-title",
                      ),
                      errorTitle: t(
                        "vessel-management.notifications.delegation-accept-error-title",
                      ),
                    })
                  }
                  sx={{ textTransform: "none" }}
                />
              ) : null}
              {isDelegateButtonShown ? (
                <Button
                  data-testid={`${TEST_ID_BASE}-delegate`}
                  label={t(`${LANG_BASE}.delegate`)}
                  variant="outlined"
                  startIcon={<SupervisedUserCircleOutlinedIcon />}
                  onClick={() => setIsDelegateDrawerOpen(true)}
                  sx={{ textTransform: "none" }}
                />
              ) : null}
              {isAcceptRejectButtonShown ? (
                <Button
                  label={t(`${LANG_BASE}.reject`)}
                  variant="outlined"
                  data-testid={`${TEST_ID_BASE}-reject-button`}
                  startIcon={<HighlightOffIcon />}
                  onClick={() => setDrawerType("reject")}
                  sx={{ textTransform: "none" }}
                />
              ) : null}
              {isAcceptRejectButtonShown ? (
                <Button
                  label={t(`${LANG_BASE}.approve`)}
                  variant="contained"
                  data-testid={`${TEST_ID_BASE}-approve-button`}
                  startIcon={<ContentPasteGoIcon />}
                  onClick={() => {
                    if (details?.bunkerRegistered) {
                      setBunkerBargeDrawerType("approve");
                    } else {
                      setDrawerType("approval");
                    }
                  }}
                  sx={{ textTransform: "none" }}
                />
              ) : null}
              {details?.vesselRegistrationStatus === VESSEL_STATUS.APPROVED &&
              !hasExistingVesselCall &&
              checkPermission(userActionPermissions, "canWriteVesselCall") &&
              !isCurrentUserOwnersMatters ? (
                <Button
                  label={t(`${LANG_BASE}.addVesselCall`)}
                  variant="contained"
                  data-testid={`${TEST_ID_BASE}-add-vessel-call-button`}
                  startIcon={<ControlPointIcon />}
                  sx={{ textTransform: "none" }}
                  onClick={handleAddVesselCall}
                />
              ) : null}
              {isDiscardButtonShown ? (
                <Button
                  data-testid={`${TEST_ID_BASE}-discard`}
                  label={t(`${LANG_BASE}.discard`)}
                  variant="text"
                  onClick={() => setIsConfirmDiscardDraftOpen(true)}
                  sx={{ textTransform: "none" }}
                />
              ) : null}
              {isEditButtonShown ? (
                <Button
                  data-testid={`${TEST_ID_BASE}-edit`}
                  label={t(`${LANG_BASE}.edit`)}
                  variant="outlined"
                  onClick={handleUpdate}
                  startIcon={<SyncIcon />}
                  sx={{ textTransform: "none" }}
                />
              ) : null}
            </div>
          </div>
        }
      />
      {isDelegateAlertShown && (
        <Alert type="info">
          <>
            {t(`${LANG_BASE}.task-delegation-info`, {
              role: t(`roles.${RolesMapping[details?.task.assignedToRole || ""]}`),
            })}
            <span className="text-base ml-2">–</span>
            <Button
              variant="text"
              label={t(`${LANG_BASE}.view-reason`)}
              sx={{ textTransform: "none", fontWeight: "bold" }}
              onClick={() => setIsDelegateDetailsDrawerOpen(true)}
            />
          </>
        </Alert>
      )}
      {isDraftAlertShown && (
        <Alert type="info">
          {t(`common.draft-info`, {
            date: details?.draftExpiresAt ? formatDateToDDMMYYYY(details?.draftExpiresAt) : "-",
          })}
        </Alert>
      )}
      <div data-testid={`${TEST_ID_BASE}`} className="flex flex-col w-full h-full p-8 px-24">
        <div className="grid gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Paper variant="outlined" sx={{ height: "100%" }}>
              <div data-testid={`${TEST_ID_BASE}-section-vessel-details`}>
                <p className="font-bold">{t(`${vesselDetailsTrans}.vessel-details`)}</p>

                <PaperItem
                  testId={`${TEST_ID_BASE}-imo-number`}
                  translate={t(`${vesselDetailsTrans}.IMO-number`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperFirstStyleClass}
                  text={details?.vesselImoNumber ?? "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-official-number`}
                  translate={t(`${vesselDetailsTrans}.official-number`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.officialNumber || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-vessel-name`}
                  translate={t(`${vesselDetailsTrans}.vessel-name`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.vesselName || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-vessel-call-sign`}
                  translate={t(`${vesselDetailsTrans}.call-sign`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.vesselCallSign || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-ship-build-year`}
                  translate={t(`${vesselDetailsTrans}.ship-build-year`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.shipBuildYear || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-date-of-delivery`}
                  translate={t(`${vesselDetailsTrans}.date-of-delivery`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={
                    details?.dateOfDelivery ? formatDateToDDMMYYYY(details.dateOfDelivery) : "-"
                  }
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-port-of-registration`}
                  translate={t(`${vesselDetailsTrans}.port-of-registration`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.port?.portName || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-flag`}
                  translate={t(`${vesselDetailsTrans}.flag`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={
                    details?.country?.countryCode
                      ? `${details?.country.countryCode} - ${details?.country.countryName}`
                      : "-"
                  }
                />
              </div>
            </Paper>
            <Paper variant="outlined" sx={{ height: "100%" }}>
              <div data-testid={`${TEST_ID_BASE}-section-technical-details`}>
                <p className="font-bold">{t(`${techDetailsTrans}.vessel-particulars`)}</p>

                <PaperItem
                  testId={`${TEST_ID_BASE}-gt`}
                  translate={t(`${techDetailsTrans}.gt`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperFirstStyleClass}
                  text={details?.grt || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-nt`}
                  translate={t(`${techDetailsTrans}.nt`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.nrt || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-dwt`}
                  translate={t(`${techDetailsTrans}.dwt`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.dwt || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-loa`}
                  translate={t(`${techDetailsTrans}.loa`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.loa || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-beam`}
                  translate={t(`${techDetailsTrans}.beam`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.beam || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-vessel-category`}
                  translate={t(`${techDetailsTrans}.vessel-category`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={getVesselCategory(details?.dwt || 0) || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-vessel-classification-society`}
                  translate={t(`${techDetailsTrans}.vessel-classification-society`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={
                    details?.otherVesselClassificationSociety
                      ? details?.otherVesselClassificationSociety
                      : details?.vesselClassificationSociety?.vesselClassificationSocietyDesc
                        ? `${details?.vesselClassificationSociety?.vesselClassificationSocietyCode} - ${details?.vesselClassificationSociety?.vesselClassificationSocietyDesc?.trim()}`
                        : "-"
                  }
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-max-draft`}
                  translate={t(`${techDetailsTrans}.max-draft`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.draftMax || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-vessel-category`}
                  translate={t(`${vesselDetailsTrans}.vessel-category-description`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.vesselType?.vesselTypeDesc || "-"}
                />
              </div>
            </Paper>

            <Paper variant="outlined">
              <div data-testid={`${TEST_ID_BASE}-section-ownership`}>
                <p className="font-bold">{t(`${ownerShipTrans}.ownership`)}</p>
                <PaperItem
                  testId={`${TEST_ID_BASE}-ship-ownership`}
                  translate={t(`${ownerShipTrans}.ship-owner`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperFirstStyleClass}
                  text={details?.shipOwnership?.shipsOwner || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-ship-charterer`}
                  translate={t(`${ownerShipTrans}.ship-charterer`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.shipOwnership?.shipsCharterer || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-ships-management`}
                  translate={t(`${ownerShipTrans}.ships-management`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.shipOwnership?.shipsManagement || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-ships-registered-owner`}
                  translate={t(`${ownerShipTrans}.ships-registered-owner`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.shipOwnership?.shipsRegisteredOwner || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-name-of-pi-club`}
                  translate={t(`${ownerShipTrans}.name-of-p&i-club`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.shipOwnership?.piClub?.piClubName || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-mlc-of-pi-club`}
                  translate={t(`${ownerShipTrans}.name-mlc-of-p&i-club`)}
                  textStyles="font-normal"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={details?.shipOwnership?.mlcPiClub?.piClubName || "-"}
                />
              </div>
            </Paper>
          </div>

          <div className="mt-6">
            <Paper variant="outlined">
              <PaperItem
                testId={`${TEST_ID_BASE}-registration-remarks`}
                translate={t("vessel-management.remarks-instructions")}
                textStyles="font-normal"
                tranStyle={transStyleClass}
                wrapperStyle={wrapperFirstStyleClass}
                text={details?.remarks || "-"}
              />
            </Paper>
          </div>

          {details?.vesselCertificates?.length ? (
            <div className="mt-6">
              <h2
                className="text-xl text-[#646A77] mb-4"
                data-testid={`${TEST_ID_BASE}-certificates-title`}
              >
                {t(`${certificatesTrans}.certificates`)}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {details?.vesselCertificates?.map(
                  (
                    {
                      certificateUrl,
                      certificateDocumentName,
                      certificateClassName,
                      certificateExpiryDate,
                      expiryStatus,
                      certificateType,
                      uploadedAt,
                      permanent,
                    },
                    idx,
                  ) => (
                    <div key={`${certificateType}-${idx}`} className="w-full">
                      <DocumentCard
                        data-testid={`${TEST_ID_BASE}-certificate-${certificateType}`}
                        title={t(`vessel-management.certificates.${certificateType}`)}
                        fileName={certificateDocumentName}
                        expiryDate={certificateExpiryDate}
                        expiryStatus={expiryStatus}
                        permanent={details.markedAsDraft ? permanent : !certificateExpiryDate}
                        className={certificateClassName || "-"}
                        uploadDate={uploadedAt}
                        onDownload={
                          certificateUrl
                            ? () =>
                                handleDownload(
                                  "certificate",
                                  "bff-vessel",
                                  certificateUrl,
                                  certificateDocumentName,
                                )
                            : undefined
                        }
                        onPreview={() => handlePreview("certificate", "bff-vessel", certificateUrl)}
                      />
                    </div>
                  ),
                )}
              </div>
            </div>
          ) : null}
          {(details?.additionalDocuments?.length ?? 0) > 0 && (
            <div className="mt-6">
              <h2
                className="text-xl text-[#646A77] mb-4"
                data-testid={`${TEST_ID_BASE}-additional-documents-title`}
              >
                {t(`${LANG_BASE}.additional-documents`)}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {details?.additionalDocuments?.map((document, idx) => {
                  const documentUrl = document.documentUrl;
                  const fileName = document.fileName;
                  const documentName = document.documentName;

                  return (
                    <div key={`additional-document-${idx}`} className="w-full">
                      <DocumentCard
                        data-testid={`${TEST_ID_BASE}-additional-document-${idx}`}
                        fileName={documentName || fileName}
                        additionalFields={{
                          [t("vessel-management.document-type")]:
                            getAdditionalDocumentTypeLabel(document),
                          ...(document.documentSummary
                            ? {
                                [t("vessel-management.document-summary")]: document.documentSummary,
                              }
                            : {}),
                        }}
                        startDate={document.documentIssueDate}
                        startDateTrans={`${t(`${bunkerBargeTrans}.issue-date`)}: `}
                        expiryDate={document.documentExpiryDate}
                        expiryDateTrans={`${t(`${bunkerBargeTrans}.expiry-date`)}: `}
                        uploadDate={document.uploadedAt}
                        onDownload={() =>
                          handleDownload("document", service, documentUrl, fileName || documentName)
                        }
                        onPreview={() => handlePreview("document", service, documentUrl)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {details?.bunkerRegistered && (
            <Box
              sx={{
                backgroundColor: "#F1F2F4",
                border: "1px solid #F1F2F4",
                borderRadius: 1,
                p: 2,
              }}
            >
              <section
                data-testid={`${TEST_ID_BASE}-bunker-barge-section`}
                className="flex flex-col gap-4"
              >
                <p className="font-bold text-base">{t("vessel-management.bunker-barge")}</p>

                <Paper variant="outlined" sx={{ backgroundColor: "#FFFFFF", p: 0 }}>
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    {renderBunkerBargeInfoItem({
                      testId: `${TEST_ID_BASE}-nature-of-service`,
                      label: t(`${bunkerBargeTrans}.nature-of-service`),
                      value: bunkerBargeDetails?.natureOfServiceCode,
                    })}
                    {renderBunkerBargeInfoItem({
                      testId: `${TEST_ID_BASE}-bunkering-company`,
                      label: t(`${bunkerBargeTrans}.company-name`),
                      value: getBunkerBargeCompanyName(bunkerBargeDetails?.bunkeringCompany),
                    })}
                  </div>
                </Paper>

                <Paper variant="outlined" sx={{ backgroundColor: "#FFFFFF", p: 0 }}>
                  <p className="font-bold px-3 pt-4">
                    {t(`${bunkerBargeTrans}.form-sections.effective-dates`)}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                    {renderBunkerBargeInfoItem({
                      testId: `${TEST_ID_BASE}-effective-start-date`,
                      label: t(`${LANG_BASE}.start-date`),
                      value: formatBunkerBargeDate(bunkerBargeDetails?.effectiveStartDate),
                    })}
                    {renderBunkerBargeInfoItem({
                      testId: `${TEST_ID_BASE}-effective-end-date`,
                      label: t(`${LANG_BASE}.end-date`),
                      value: formatBunkerBargeDate(bunkerBargeDetails?.effectiveEndDate),
                    })}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 px-3">
                    <div className="border-b border-[#F1F2F4]" />
                    <div className="hidden md:block border-b border-[#F1F2F4]" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 pt-3">
                    {renderBunkerBargeInfoItem({
                      testId: `${TEST_ID_BASE}-remarks`,
                      label: t(`${LANG_BASE}.remarks`),
                      value: bunkerBargeDetails?.remarks || "-",
                    })}
                    {bunkerBargeDetails?.gracePeriod ? (
                      renderBunkerBargeInfoItem({
                        testId: `${TEST_ID_BASE}-grace-period`,
                        label: t(`${LANG_BASE}.grace-period`),
                        value: formatBunkerBargeDate(bunkerBargeDetails.gracePeriod),
                      })
                    ) : (
                      <div className="hidden md:block" aria-hidden="true" />
                    )}
                  </div>
                </Paper>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderBunkerBargeCertificateCard({
                    certificateType: "NAVIGATION_LICENSE",
                  })}
                  {renderBunkerBargeCertificateCard({
                    certificateType: "CHARTER_PARTY_AGREEMENT",
                    additionalFields: {
                      [t(`${bunkerBargeTrans}.register-owner`)]: bunkerBargeDetails?.charterOwner,
                    },
                  })}
                  {renderBunkerBargeCertificateCard({
                    certificateType: "OIL_SPILL_COMPANY",
                    additionalFields: {
                      [t(`${bunkerBargeTrans}.company-name`)]: getBunkerBargeCompanyName(
                        bunkerBargeDetails?.oilCompany,
                      ),
                    },
                  })}
                  {renderBunkerBargeCertificateCard({
                    certificateType: "SIRE_REPORT",
                  })}
                  {renderBunkerBargeCertificateCard({
                    certificateType: "HOSE_TEST_CERTIFICATE",
                    expiryLabel: t(`${bunkerBargeTrans}.expiry-date`),
                    additionalFields: {
                      [t(`${bunkerBargeTrans}.company-name`)]:
                        bunkerBargeDetails?.companyNameHoseTestCertificate,
                    },
                  })}
                  {renderBunkerBargeCertificateCard({
                    certificateType: "SHIP_TO_SHIP_PLAN",
                  })}
                </div>

                {bunkerBargeAdditionalDocuments.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <p className="font-bold text-base">
                      {t(`${bunkerBargeTrans}.form-sections.additional-documents`)}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {bunkerBargeAdditionalDocuments.map((document, idx) => (
                        <DocumentCard
                          key={`${document.documentUrl}-${idx}`}
                          data-testid={`${TEST_ID_BASE}-bunker-barge-additional-document-${idx}`}
                          fileName={document.documentName || document.fileName}
                          rootClassName="bg-white"
                          additionalFields={{
                            [t("vessel-management.document-type")]:
                              getBunkerBargeDocumentTypeLabel(document),
                            ...(document.documentSummary
                              ? {
                                  [t("vessel-management.document-summary")]:
                                    document.documentSummary,
                                }
                              : {}),
                          }}
                          startDate={document.documentIssueDate}
                          startDateTrans={`${t(`${bunkerBargeTrans}.issue-date`)}: `}
                          expiryDate={document.documentExpiryDate}
                          expiryDateTrans={`${t(`${bunkerBargeTrans}.expiry-date`)}: `}
                          uploadDate={document.uploadedAt}
                          onDownload={() =>
                            handleDownload(
                              "document",
                              service,
                              document.documentUrl,
                              document.documentName,
                            )
                          }
                          onPreview={() => handlePreview("document", service, document.documentUrl)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </section>
            </Box>
          )}
        </div>
      </div>
      <DetailsSideDrawer
        isOpen={isDetailsDrawerOpen}
        onClose={() => {
          setIsDetailsDrawerOpen(false);
          setDrawerType(null);
        }}
        submittedBy={isStatusDetailsDrawer ? statusChangeSubmittedBy : submittedBy || createdBy}
        submittedByRole={isStatusDetailsDrawer ? statusChangeSubmittedByRoleValue : createdByRole}
        submittedAt={isStatusDetailsDrawer ? statusChangeTime : createdAt}
        externalNotes={isStatusDetailsDrawer ? undefined : externalNotes}
        internalNotes={isStatusDetailsDrawer ? undefined : internalNotes}
        externalAttachmentUrl={isStatusDetailsDrawer ? undefined : externalNotesDocument}
        externalAttachmentName={isStatusDetailsDrawer ? undefined : externalNotesDocumentName}
        internalAttachmentUrl={isStatusDetailsDrawer ? undefined : internalNotesDocument}
        internalAttachmentName={isStatusDetailsDrawer ? undefined : internalNotesDocumentName}
        statusChangeReason={isStatusDetailsDrawer ? details?.statusChangeReason : undefined}
        isRestrictionStatusSidedrawer={isStatusDetailsDrawer}
      />
      <RestrictionStatusSideDrawer
        isOpen={isRestrictionStatusDrawerOpen}
        onClose={() => setIsRestrictionStatusDrawerOpen(false)}
        onSuccess={() => {
          setIsRestrictionStatusDrawerOpen(false);
          handleSuccess();
        }}
        vesselId={details?.id || ""}
        dropdownCurrentOption={details?.vesselLogType?.vesselLogTypeCode}
      />
      <DelegateSideDrawer
        isOpen={isDelegateDrawerOpen}
        onClose={() => setIsDelegateDrawerOpen(false)}
        vesselId={id || ""}
      />
      <DelegateDetailsSideDrawer
        isOpen={isDelegateDetailsDrawerOpen}
        onClose={() => setIsDelegateDetailsDrawerOpen(false)}
        delegatedBy={details?.task?.submittedBy}
        delegatedByRole={details?.task?.submittedByRole}
        delegationReason={details?.task?.internalNotes}
        attachment={details?.task?.internalNotesDocument}
        attachmentName={details?.task?.internalNotesDocumentName}
      />
      <ApproveUpdateBunkerBargeSideDrawer
        isOpen={bunkerBargeDrawerType !== null}
        onClose={() => setBunkerBargeDrawerType(null)}
        vesselId={id || ""}
        isUpdate={isBunkerBargeUpdate}
        startDate={bunkerBargeDetails?.effectiveStartDate}
        endDate={bunkerBargeDetails?.effectiveEndDate}
        gracePeriod={isBunkerBargeUpdate ? bunkerBargeDetails?.gracePeriod : undefined}
        remarks={bunkerBargeDetails?.remarks}
        internalNotes={isBunkerBargeUpdate ? internalNotes : undefined}
        internalNotesDocument={isBunkerBargeUpdate ? internalNotesDocument : undefined}
        internalNotesDocumentName={isBunkerBargeUpdate ? internalNotesDocumentName : undefined}
        externalNotes={isBunkerBargeUpdate ? externalNotes : undefined}
        externalNotesDocument={isBunkerBargeUpdate ? externalNotesDocument : undefined}
        externalNotesDocumentName={isBunkerBargeUpdate ? externalNotesDocumentName : undefined}
        version={bunkerBargeDetails?.version ?? 0}
      />
      <ApproveRejectSideDrawer
        isOpen={drawerType === "approval" || drawerType === "reject"}
        onClose={() => setDrawerType(null)}
        vesselId={id || ""}
        type={drawerType === "approval" ? "approval" : "reject"}
      />
      <GenericDialog
        open={isConfirmDiscardDraftOpen}
        title={t(`${LANG_BASE}.discard-title`)}
        content={t(`${LANG_BASE}.discard-description`)}
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t(`${LANG_BASE}.discard`)}
        handleCloseDialog={() => setIsConfirmDiscardDraftOpen(false)}
        confirmAction={onDiscard}
        testId="discard-draft-confirm-dialog"
        maxWidth="sm"
      />
    </PageLayout>
  );
};

export default VesselStatus;
